import {
  AlignmentType,
  ShadingType,
  Table,
  TableRow,
  WidthType,
} from 'docx'
import { formatDateRuIfDateString } from '@/lib/datetime'
import { trimTrailingEmptyColumns } from '@/components/expAnalogs/taLp/parseWorkbook'
import {
  createBoldParagraph,
  createParagraph,
  createTableCell,
  DEFAULT_TABLE_MARGINS,
} from '../docxFunctions'

const HEADER_SHADING = {
  type: ShadingType.SOLID,
  color: 'D9D9D9',
}

function formatIntegerWithSpaces(intStr) {
  const s = String(intStr ?? '').trim()
  const neg = s.startsWith('-')
  const d = neg ? s.slice(1) : s
  if (!/^\d+$/.test(d)) return s
  const grouped = d.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return neg ? `-${grouped}` : grouped
}

function formatCellValue(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const formattedDate = formatDateRuIfDateString(raw)
  if (formattedDate !== raw) return formattedDate
  let m = raw.match(/^(-?\d+)[.,](\d+)$/)
  if (m) {
    const intPart = formatIntegerWithSpaces(m[1])
    const decPart = m[2].slice(0, 2).padEnd(2, '0')
    return `${intPart},${decPart}`
  }
  m = raw.match(/^(-?\d+)$/)
  if (m) return formatIntegerWithSpaces(m[1])
  return raw
}

function rowValues(row) {
  return (row || [])
    .map(cell => String(cell?.value ?? '').trim())
    .filter(Boolean)
}

function isSectionRow(row) {
  const text = rowValues(row).join(' ').toLowerCase()
  return (
    text.includes('предлагаемый лекарственный препарат') ||
    text.includes('терапевтически аналогичные')
  )
}

function isHeaderRow(row, rowIndex) {
  if (isSectionRow(row)) return false
  const text = rowValues(row).join(' ').toLowerCase()
  if (rowIndex <= 2 && text.includes('мнн')) return true
  if (text.includes('объемы государственных закупок')) return true
  if (text.includes('объёмы государственных закупок')) return true
  if (text.includes('упаковки') && text.includes('млн')) return true
  return false
}

function isMergedCell(cell) {
  if (!cell) return false
  return (Number(cell.rowspan) || 1) > 1 || (Number(cell.colspan) || 1) > 1
}

function createDocxTableRow(row, rowIndex) {
  const section = isSectionRow(row)
  const header = isHeaderRow(row, rowIndex)
  const cells = []

  for (const cell of row || []) {
    if (!cell || cell.hidden) continue
    const merged = isMergedCell(cell)
    const text = formatCellValue(cell.value)
    const bold = section || header || merged

    cells.push(
      createTableCell({
        rowSpan: cell.rowspan > 1 ? cell.rowspan : undefined,
        columnSpan: cell.colspan > 1 ? cell.colspan : undefined,
        shading: section || header ? HEADER_SHADING : undefined,
        verticalAlign: 'center',
        children: bold
          ? [
              createBoldParagraph(
                text,
                { alignment: AlignmentType.CENTER },
                18
              ),
            ]
          : [
              createParagraph(
                text,
                { alignment: AlignmentType.CENTER },
                {},
                18
              ),
            ],
      })
    )
  }

  return cells.length ? new TableRow({ children: cells }) : null
}

/**
 * Таблица из сабэкспертизы «Данные о ТА ЛП (РУ, ГО, ГЗ)» для п. 5.1 заключения КО.
 * @param {{ rows?: Array, note?: string }} taLp
 */
export function createTaLpTable(taLp) {
  const tableRows = trimTrailingEmptyColumns(
    Array.isArray(taLp?.rows) ? taLp.rows : []
  )
  if (!tableRows.length) {
    return [
      createParagraph(
        'Данные о государственной регистрации, обращении и объемах государственных закупок не загружены (сабэкспертиза «Данные о ТА ЛП»).',
        { alignment: AlignmentType.LEFT },
        { italics: true },
        20
      ),
    ]
  }

  const docxRows = tableRows
    .map((row, idx) => createDocxTableRow(row, idx))
    .filter(Boolean)

  if (!docxRows.length) {
    throw new Error('[createTaLpTable] Не удалось построить строки таблицы ТА ЛП')
  }

  const blocks = [
    new Table({
      alignment: AlignmentType.JUSTIFIED,
      margins: DEFAULT_TABLE_MARGINS,
      width: {
        size: 100,
        type: WidthType.PERCENTAGE,
      },
      rows: docxRows,
    }),
  ]

  const note = String(taLp?.note || '').trim()
  if (note) {
    for (const line of note.split('\n').map(s => s.trim()).filter(Boolean)) {
      blocks.push(
        createParagraph(
          line,
          { alignment: AlignmentType.LEFT, spacing: { before: 60, after: 0 } },
          { italics: true },
          18
        )
      )
    }
  }

  return blocks
}
